import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@taiga-platform/ui";
import { Callout, Details } from "../components";

export function Opportunity() {
  return (
    <>
      <h1>The Opportunity</h1>

      <h2>Every platform shift creates a defining company</h2>
      <p>
        The internet created Google. Cloud created AWS. Each shift was obvious
        in retrospect, invisible to most while it was happening, and worth
        trillions to whoever owned the defining layer.
      </p>
      <p>
        The agentic AI shift is underway. And the layer that will define it —
        governance — is wide open.
      </p>

      <table>
        <thead>
          <tr>
            <th>Era</th>
            <th>Period</th>
            <th>Shift</th>
            <th>Winner</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Internet</td>
            <td>1990s</td>
            <td>Connected the world</td>
            <td>Google / Amazon</td>
          </tr>
          <tr>
            <td>Cloud</td>
            <td>2010s</td>
            <td>On-demand infrastructure</td>
            <td>AWS / Azure</td>
          </tr>
          <tr>
            <td>
              <strong>Agentic AI</strong>
            </td>
            <td>
              <strong>2025+</strong>
            </td>
            <td>
              <strong>Autonomous work</strong>
            </td>
            <td>
              <strong>?</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        The question is not whether AI will build software autonomously. It
        already does. The question is who governs it when it does.
      </p>

      <hr />

      <h2>The numbers are staggering</h2>
      <p>
        The AI services market is projected at $5.6 trillion (Gartner). 75% of
        VC funding is shifting to AI by 2026. Capital is flooding in at a pace
        that dwarfs previous cycles.
      </p>
      <p>
        Look at the deal trajectory: the average AI code assistant deal size
        grew from $7.4M in 2022 to $527.8M in 2025 — a 71x increase in three
        years. The AI coding segment alone commands more than $4B in spend,
        making it the most crowded segment in enterprise AI (Bessemer State of
        AI 2025).
      </p>

      <Callout variant="quote">Money is not the constraint. Direction is.</Callout>

      <hr />

      <h2>Most of it will fail</h2>
      <p>
        60% of GenAI projects fail (Gartner). 95% of generative AI pilots fail
        to achieve measurable P&amp;L impact (MIT, Jul 2025).
      </p>

      <Callout variant="danger" title="Root Cause">
        Not because the technology does not work. Because nobody governs it.
      </Callout>

      <p>
        Three forces are converging to make this problem unavoidable.
      </p>
      <p>
        <strong>The Copilot-to-Agent shift.</strong> Companies are moving from
        copilots (human-in-the-loop) to agents (autonomous execution). When AI
        systems act on their own, the demand for governance infrastructure
        does not grow linearly — it explodes. Someone must ensure agents
        follow organizational policies, produce auditable outputs, and align
        with business objectives. Today, nobody does.
      </p>
      <p>
        <strong>Enterprise adoption barriers.</strong> The root cause of AI
        project failure is not the AI itself. It is missing governance,
        missing risk management, missing business alignment. Enterprises need
        a platform that connects strategy to execution, not another coding
        assistant. The gap between "demo-ready" and "enterprise-ready" is
        where billions of dollars go to die.
      </p>
      <p>
        <strong>The EU AI Act.</strong> Compliance becomes mandatory in 2026.
        Penalties run up to EUR 35M or 7% of global turnover. This is not a
        future concern — it is a current procurement requirement. The
        regulatory tailwind creates mandatory demand for governance tooling
        and a structural moat for companies that build compliance into their
        architecture from day one.
      </p>

      <hr />

      <h2>The Productivity Paradox</h2>
      <p>
        The industry claims 30-55% productivity gains from AI coding tools.
        The data tells a different story.
      </p>

      <h3>What the research actually shows</h3>
      <table>
        <thead>
          <tr>
            <th>Source</th>
            <th>Sample</th>
            <th>Finding</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>METR RCT (Jul 2025)</td>
            <td>
              16 experienced devs, 246 real issues, randomized controlled
              trial
            </td>
            <td>
              Developers were <strong>19% slower</strong> with AI tools. Yet
              they estimated they were 20% faster. Developers accepted &lt;44%
              of AI-generated code; time reviewing and rejecting ate the
              gains.
            </td>
          </tr>
          <tr>
            <td>DX / Laura Tacho</td>
            <td>121,000 developers</td>
            <td>
              Productivity gains plateaued at <strong>~10%</strong> since
              initial rollout
            </td>
          </tr>
          <tr>
            <td>Faros AI</td>
            <td>10,000+ developers</td>
            <td>
              21% more tasks, 98% more PRs — but PR review time{" "}
              <strong>+91%</strong>, PR size <strong>+154%</strong>, bugs/dev{" "}
              <strong>+9%</strong>. No correlation between AI adoption and
              company-level improvement.
            </td>
          </tr>
          <tr>
            <td>BCG</td>
            <td>Cross-industry</td>
            <td>
              60% of companies generate <strong>no material value</strong>{" "}
              from AI. Only 5% create substantial value at scale.
            </td>
          </tr>
          <tr>
            <td>Bain</td>
            <td>Cross-industry</td>
            <td>
              10-15% productivity boosts, rarely redirected to higher-value
              work. Coding is &lt;40% of an engineer's workday, capping
              impact.
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        The METR study is the most rigorous to date — a proper randomized
        controlled trial, not a self-reported survey. Its finding is striking:
        developers <em>believed</em> they were faster while actually being
        slower. The gap between perception and reality explains why vendor
        claims and user testimonials diverge so sharply from measured
        outcomes.
      </p>

      <h3>The AI code quality crisis</h3>
      <p>Adoption is near-universal. Quality is deteriorating.</p>

      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Developers using AI coding assistants monthly</td>
            <td>92.6%</td>
            <td>DX Research, 121,000 devs</td>
          </tr>
          <tr>
            <td>Committed code that is AI-generated</td>
            <td>42%</td>
            <td>Sonar 2026, 1,100+ organizations</td>
          </tr>
          <tr>
            <td>Engineers using AI for half or more of their work</td>
            <td>75%</td>
            <td>Pragmatic Engineer 2026</td>
          </tr>
          <tr>
            <td>Agent-generated PRs containing vulnerabilities</td>
            <td>87%</td>
            <td>DryRun Security</td>
          </tr>
          <tr>
            <td>AI code vulnerability multiplier vs human code</td>
            <td>2.74x</td>
            <td>Veracode 2025</td>
          </tr>
          <tr>
            <td>Developer trust in AI tools (trending)</td>
            <td>Dropped from 70%+ to 60%</td>
            <td>Stack Overflow 2025</td>
          </tr>
        </tbody>
      </table>

      <p>
        The pattern: adoption is accelerating while trust is declining.
        Developers use AI tools daily but increasingly doubt their output.
        This is the governance gap made visible — tools that generate more
        code, faster, with less oversight, producing more vulnerabilities.
      </p>

      <p>The market dynamics reinforce this:</p>
      <ul>
        <li>Seed-stage AI funding dropped 40% YoY (ainvest.com, Jan 2026)</li>
        <li>
          60-70% of "AI wrapper" startups generate zero revenue; 90% projected
          to fail by 2026
        </li>
        <li>
          The survivors will be companies with genuine moats — not thin layers
          on top of foundation models
        </li>
      </ul>

      <h3>Where the gains disappear</h3>
      <p>
        The Faros data is particularly revealing. More PRs and more tasks
        sounds like progress. But when PR review time nearly doubles, PR size
        inflates by 154%, and bug rates climb, the system is producing more{" "}
        <em>volume</em> without more <em>value</em>. The bottleneck has
        shifted from code generation to code review, testing, and integration
        — exactly the stages that current tools ignore.
      </p>
      <p>
        The industry's productivity claims are built on narrow benchmarks that
        measure code generation in isolation. Real-world data shows AI coding
        tools speed up the easy parts but create new bottlenecks in review,
        testing, and integration. The missing piece is not faster code
        generation — it is governed end-to-end delivery.
      </p>

      <hr />

      <h2>The gap nobody fills</h2>
      <p>
        Billions have been poured into AI development tools. Not one of them
        solves governance.
      </p>

      <table>
        <thead>
          <tr>
            <th>Segment</th>
            <th>Examples</th>
            <th>What They Do</th>
            <th>What's Missing</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>AI Coding Tools</td>
            <td>Cursor, Copilot, Lovable</td>
            <td>Help individuals code faster</td>
            <td>Zero enterprise governance</td>
          </tr>
          <tr>
            <td>AI Dev Suites</td>
            <td>Devin, Kilo Code</td>
            <td>Autonomous code execution</td>
            <td>Limited policy enforcement</td>
          </tr>
          <tr>
            <td>Consultancies</td>
            <td>Reaktor, Accenture</td>
            <td>Full-service delivery</td>
            <td>Slow, expensive, human-dependent</td>
          </tr>
          <tr>
            <td>Workflow Tools</td>
            <td>Linear, Jira</td>
            <td>Organize and track</td>
            <td>Don't execute</td>
          </tr>
          <tr>
            <td>Hyperscalers</td>
            <td>AWS, Azure, GCP</td>
            <td>Infrastructure</td>
            <td>No code-level governance</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="quote">
        No one combines consultancy-grade governance with AI-native speed.
        That is TAIGA's position.
      </Callout>

      <h3>Capability matrix</h3>
      <table>
        <thead>
          <tr>
            <th>Capability</th>
            <th>TAIGA</th>
            <th>Consultancies</th>
            <th>AI Tools</th>
            <th>AI Dev Suites</th>
            <th>Workflow</th>
            <th>Hyperscalers</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Strategy-to-Code</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
            <td>Partial</td>
            <td>Partial</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Policy Enforcement</td>
            <td>Yes</td>
            <td>Partial</td>
            <td>No</td>
            <td>Partial</td>
            <td>No</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Autonomous Execution</td>
            <td>Yes</td>
            <td>No</td>
            <td>Partial</td>
            <td>Yes</td>
            <td>No</td>
            <td>Partial</td>
          </tr>
          <tr>
            <td>Outcome Pricing</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
            <td>No</td>
            <td>No</td>
            <td>No</td>
          </tr>
          <tr>
            <td>Full Lifecycle</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
            <td>Partial</td>
            <td>Partial</td>
            <td>Partial</td>
          </tr>
          <tr>
            <td>Shadow AI Prevention</td>
            <td>Yes</td>
            <td>Partial</td>
            <td>No</td>
            <td>Partial</td>
            <td>—</td>
            <td>—</td>
          </tr>
        </tbody>
      </table>

      <p>
        Consultancies cover the lifecycle but lack speed and scalability. AI
        tools are fast but ungoverned. Hyperscalers provide infrastructure but
        not outcomes. TAIGA is the only player that spans all six.
      </p>

      <hr />

      <h2>The competitive landscape</h2>
      <p>
        Billions of dollars have been invested in AI coding tools. None of
        them solve governance.
      </p>

      <Details variant="note" title="Tier 1 — IDE Copilots & Code Editors">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>HQ</TableHead>
              <TableHead>Funding</TableHead>
              <TableHead>Valuation</TableHead>
              <TableHead>Key Fact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">GitHub Copilot (Microsoft)</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>N/A (Microsoft subsidiary)</TableCell>
              <TableCell>N/A</TableCell>
              <TableCell>4.7M paid subscribers (+75% YoY). ~90% Fortune 100. Coding agent GA Sep 2025. No governance layer.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Cursor (Anysphere)</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$3.5B total; $2.3B Series D Nov 2025</TableCell>
              <TableCell>$29.3B; seeking ~$50B</TableCell>
              <TableCell>~1,428 employees. 1M+ DAU. 60% rev from enterprise. 50%+ Fortune 500</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Augment Code</TableCell>
              <TableCell>Palo Alto</TableCell>
              <TableCell>$252M; $227M Series B Apr 2024</TableCell>
              <TableCell>~$977M</TableCell>
              <TableCell>~188 employees. Enterprise codebase awareness + real-time team sync</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Tabnine</TableCell>
              <TableCell>Tel Aviv</TableCell>
              <TableCell>~$67-102M; $8M Apr 2025</TableCell>
              <TableCell>Not disclosed</TableCell>
              <TableCell>Laid off 18%, dropped free tier Apr 2025. On-prem/air-gapped focus</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <Details variant="note" title="Tier 2 — Autonomous Agents">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>HQ</TableHead>
              <TableHead>Funding</TableHead>
              <TableHead>Valuation</TableHead>
              <TableHead>Key Fact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Cognition (Devin)</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$696M+; $400M at $10.2B Sep 2025</TableCell>
              <TableCell>$10.2B</TableCell>
              <TableCell>
                Acquired Windsurf product for ~$250M. Clients: Goldman Sachs,
                Citi, Dell, Cisco
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Claude Code (Anthropic)</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$13.7B+ total</TableCell>
              <TableCell>$61.5B (Mar 2025)</TableCell>
              <TableCell>
                Terminal-based agentic coding. Remote Control, Plugins,
                Parallel agents. MCP becoming de facto standard. No enterprise
                governance layer.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Factory AI</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$70M+; $50M Series B Sep 2025</TableCell>
              <TableCell>$300M</TableCell>
              <TableCell>
                "Droids" for full SDLC. #1 Terminal Bench. Uses Claude
                foundation
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Sourcegraph / Amp</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$223M (no new round since 2021)</TableCell>
              <TableCell>$2.6B (2021 Series D)</TableCell>
              <TableCell>Spun out "Amp" as agentic agent co late 2025</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <Details variant="note" title="Tier 3 — App Builders ('Vibe Coding')">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>HQ</TableHead>
              <TableHead>Funding</TableHead>
              <TableHead>Valuation</TableHead>
              <TableHead>Key Fact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Lovable</TableCell>
              <TableCell>Stockholm</TableCell>
              <TableCell>$545M; $330M Series B Dec 2025</TableCell>
              <TableCell>$6.6B</TableCell>
              <TableCell>
                8M users. $0 to $100M ARR in 8 months. 100K new projects/day
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Replit</TableCell>
              <TableCell>Foster City</TableCell>
              <TableCell>$650M+; $400M Series D Mar 2026</TableCell>
              <TableCell>$9B</TableCell>
              <TableCell>
                50M+ registered users. 85% of Fortune 500 have employees on
                Replit
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Bolt.new (StackBlitz)</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$105.5M Series B Jan 2025</TableCell>
              <TableCell>~$700M</TableCell>
              <TableCell>
                $0 to $4M ARR in first month. In-browser WebContainers
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <Details variant="note" title="Tier 4 — Foundation Models for Code">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>HQ</TableHead>
              <TableHead>Funding</TableHead>
              <TableHead>Valuation</TableHead>
              <TableHead>Key Fact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Poolside AI</TableCell>
              <TableCell>SF + Paris</TableCell>
              <TableCell>$626M+; raising $2B Oct 2025</TableCell>
              <TableCell>$12B (target)</TableCell>
              <TableCell>
                Custom RLCEF models. Self-hosted for banks/defense. Building
                2GW compute campus
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Magic AI</TableCell>
              <TableCell>SF</TableCell>
              <TableCell>$465M; $320M Aug 2024</TableCell>
              <TableCell>Not confirmed</TableCell>
              <TableCell>
                Long-context architecture. No confirmed 2025-26 activity — may
                be stealth
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <Details variant="note" title="Tier 5 — Acquired / Consolidated">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Acquirer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Key Fact</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Windsurf (Codeium)</TableCell>
              <TableCell>Google (team) + Cognition (product)</TableCell>
              <TableCell>~$2.65B combined</TableCell>
              <TableCell>
                Google got leadership; Cognition got product + 350 enterprise
                customers
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Adept</TableCell>
              <TableCell>Amazon (talent)</TableCell>
              <TableCell>$330M license + $100M retention</TableCell>
              <TableCell>~66% of employees joined Amazon</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">AutoCodeRover</TableCell>
              <TableCell>SonarSource</TableCell>
              <TableCell>Undisclosed</TableCell>
              <TableCell>
                NUS spinout, top 3 SWE-Bench. Sonar establishing Singapore
                R&amp;D
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Koala</TableCell>
              <TableCell>Cursor</TableCell>
              <TableCell>Undisclosed</TableCell>
              <TableCell>
                Acqui-hire for enterprise readiness; had raised $15M 5 months
                prior
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Codegen</TableCell>
              <TableCell>ClickUp</TableCell>
              <TableCell>Undisclosed</TableCell>
              <TableCell>
                ClickUp moving from work management to autonomous execution
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Details>

      <Callout variant="warning" title="Consolidation is Accelerating">
        The FTC issued a landmark report concluding "pseudo-acquisitions"
        (reverse acqui-hires) could constitute unfair competition. Big Tech
        spent more than $40B on acqui-hires in 2024-2025. VCs predict fewer
        vendors in 2026. The window for independent players to establish
        position is narrowing.
      </Callout>

      <hr />

      <h2>Platform incumbents are moving</h2>
      <p>
        The startups listed above are not the only threat. Every major
        platform vendor is shipping agentic capabilities.
      </p>

      <table>
        <thead>
          <tr>
            <th>Platform</th>
            <th>Agentic GA?</th>
            <th>Key Moves</th>
            <th>What's Still Missing</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GitHub Copilot</td>
            <td>Yes (Sep 2025)</td>
            <td>
              4.7M paid subscribers. Coding agent GA. Mission Control shipping
              2026.
            </td>
            <td>No governance/audit trail. No EU data residency guarantee.</td>
          </tr>
          <tr>
            <td>AWS Q Developer</td>
            <td>Yes</td>
            <td>Migrated 1,000 Java apps in 2 days, saved Amazon $260M.</td>
            <td>AWS-only. No lifecycle. No outcome pricing.</td>
          </tr>
          <tr>
            <td>Google Gemini Code Assist</td>
            <td>Unclear</td>
            <td>Jules agent announced, status unclear.</td>
            <td>No public adoption metrics. No governance.</td>
          </tr>
          <tr>
            <td>JetBrains Air</td>
            <td>No (Mar 2026)</td>
            <td>New agentic IDE launched Mar 2026.</td>
            <td>IDE heritage, not platform. No compliance layer.</td>
          </tr>
          <tr>
            <td>GitLab Duo</td>
            <td>Yes (Jan 2026)</td>
            <td>Agent Platform GA. Full DevSecOps scope.</td>
            <td>No outcome pricing. Not governance-first.</td>
          </tr>
          <tr>
            <td>Anthropic Claude Code</td>
            <td>No</td>
            <td>
              Remote Control, Plugins, Parallel agents (Feb 2026).
            </td>
            <td>Terminal-based. No enterprise governance layer.</td>
          </tr>
        </tbody>
      </table>

      <p>
        All incumbents are moving toward agentic capabilities. None have
        governance-first architecture, outcome pricing, or full lifecycle
        coverage with audit trail. They are optimizing for developer velocity.
        TAIGA is building for enterprise accountability.
      </p>

      <hr />

      <h2>Enterprise buyer behavior</h2>

      <h3>Adoption is near-universal — and accelerating</h3>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>72% of organizations adopted AI in at least one function</td>
            <td>McKinsey, Dec 2024</td>
          </tr>
          <tr>
            <td>
              80% of enterprises expected to deploy AI-augmented dev tools by
              2026
            </td>
            <td>Gartner</td>
          </tr>
          <tr>
            <td>
              25%+ of new code at Google is AI-generated (with human review)
            </td>
            <td>Google</td>
          </tr>
          <tr>
            <td>
              92.6% of developers use AI coding assistants at least monthly
            </td>
            <td>DX Research, 121,000 developers</td>
          </tr>
          <tr>
            <td>51% of professional developers use AI daily</td>
            <td>Stack Overflow, 49,000+ respondents</td>
          </tr>
        </tbody>
      </table>

      <p>Adoption is not the question. Governed adoption is.</p>

      <h3>What blocks enterprise rollout</h3>
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Blocker</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>#1</td>
            <td>Security / data leakage</td>
            <td>Top concern in every enterprise survey</td>
          </tr>
          <tr>
            <td>#2</td>
            <td>IP / copyright uncertainty</td>
            <td>Legal departments actively blocking adoption</td>
          </tr>
          <tr>
            <td>#3</td>
            <td>Accuracy / hallucination</td>
            <td>15-30% of suggestions need correction</td>
          </tr>
          <tr>
            <td>#4</td>
            <td>Compliance / regulatory</td>
            <td>
              Regulated industries require audit trails; few tools provide
              them
            </td>
          </tr>
          <tr>
            <td>#5</td>
            <td>Shadow AI</td>
            <td>
              40% of enterprise devs use unsanctioned tools (Gartner 2024)
            </td>
          </tr>
          <tr>
            <td>#6</td>
            <td>ROI measurement</td>
            <td>Difficulty proving gains beyond anecdote</td>
          </tr>
          <tr>
            <td>#7</td>
            <td>Integration complexity</td>
            <td>Fitting into existing toolchains and workflows</td>
          </tr>
        </tbody>
      </table>

      <blockquote>
        "Security and IP concerns are the primary reasons enterprises cite for
        delaying AI coding assistant rollouts, not cost." — Gartner
      </blockquote>

      <p>
        Price sensitivity is low relative to perceived productivity gain. The
        constraint is security/compliance approval, not budget. This is
        precisely the gap TAIGA fills: governance that unblocks procurement,
        not another tool that procurement blocks.
      </p>

      <h3>How enterprises buy</h3>
      <ul>
        <li>
          <strong>~60% have standardized</strong> on one primary tool (usually
          Copilot)
        </li>
        <li>
          <strong>Bottom-up adoption is shifting to top-down</strong>{" "}
          centralized procurement to control shadow AI
        </li>
        <li>
          <strong>VP Eng / CTO initiates</strong> in ~70% of cases; IT/Security
          holds veto; Procurement enters for &gt;$100K ARR deals
        </li>
        <li>
          <strong>Evaluation cycles</strong> are 3-6 months for enterprise,
          driven by security review not feature comparison
        </li>
      </ul>

      <p>
        The buying motion is moving from "developer picks a tool" to "platform
        team selects a governed stack." TAIGA is built for the second motion.
      </p>

      <hr />

      <h2>Notable failures — what dies and why</h2>
      <p>
        Not every AI coding company survives. The failure patterns are
        instructive.
      </p>

      <table>
        <thead>
          <tr>
            <th>Company</th>
            <th>What Happened</th>
            <th>Lesson</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Builder.ai</td>
            <td>Insolvency, 2025</td>
            <td>Most "AI" was manual offshore labor. Authenticity matters.</td>
          </tr>
          <tr>
            <td>Korbit</td>
            <td>Shut down, 2025</td>
            <td>
              Standalone code review crushed when Copilot bundled review.
            </td>
          </tr>
          <tr>
            <td>CodeParrot</td>
            <td>Shut down</td>
            <td>MRR peaked at ~$1.5K. "Thin wrapper" could not compete.</td>
          </tr>
          <tr>
            <td>Tune AI</td>
            <td>Shut down</td>
            <td>Cloud providers shipped equivalent at lower cost.</td>
          </tr>
          <tr>
            <td>Tabnine</td>
            <td>Struggling (18% layoffs, dropped free tier)</td>
            <td>Losing mindshare despite privacy focus.</td>
          </tr>
        </tbody>
      </table>

      <p>
        The pattern is clear: 60-70% of "AI wrapper" startups generate zero
        revenue. 90% are projected to fail by 2026. Survival requires
        proprietary technology or a unique moat — not a better prompt on top
        of someone else's model.
      </p>

      <p>
        <strong>What kills startups in this space:</strong>
      </p>
      <ol>
        <li>
          <strong>Feature bundling by incumbents.</strong> If your product is
          one feature, the platform vendor will ship it for free. Korbit died
          this way.
        </li>
        <li>
          <strong>No proprietary technology.</strong> Wrappers around
          foundation models have zero defensibility. CodeParrot and Tune AI
          died this way.
        </li>
        <li>
          <strong>Fake AI.</strong> If the "AI" is actually humans, eventually
          someone notices. Builder.ai died this way.
        </li>
        <li>
          <strong>Single-vector value.</strong> Privacy alone (Tabnine) or
          speed alone is not enough when the market consolidates around
          platforms.
        </li>
      </ol>

      <Callout variant="tip" title="Not a Wrapper">
        TAIGA is not a wrapper. The governance framework (LFA), the policy
        engine, and the three-loop architecture are proprietary and cannot be
        replicated by bolting features onto a coding assistant. The moat is
        architectural, not incremental.
      </Callout>

      <hr />

      <h2>What no one has built yet</h2>
      <p>
        Despite $4B+ in AI coding spend and hundreds of funded startups,
        critical gaps remain entirely unaddressed.
      </p>

      <table>
        <thead>
          <tr>
            <th>Gap</th>
            <th>Current State</th>
            <th>TAIGA's Approach</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <strong>Business goal verification</strong>
            </td>
            <td>
              No tool measures "did the business metric actually move?" after
              code ships.
            </td>
            <td>
              LFA connects strategy to code to outcome. Every delivery traces
              back to a business objective.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Enterprise governance for AI code</strong>
            </td>
            <td>
              Audit trails, policy enforcement, compliance — the #1 enterprise
              blocker, with few solutions.
            </td>
            <td>
              Core product. Governance is the architecture, not a feature.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Full SDLC with outcome pricing</strong>
            </td>
            <td>
              Everyone charges per seat or per token. No one charges for
              shipped results.
            </td>
            <td>
              Outcome-based pricing aligns TAIGA's incentives with customer
              value.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Shadow AI prevention</strong>
            </td>
            <td>
              40% of devs use unsanctioned tools. No governance solution
              exists.
            </td>
            <td>
              Governed platform replaces shadow tools by being better{" "}
              <em>and</em> compliant.
            </td>
          </tr>
          <tr>
            <td>
              <strong>Production feedback loops</strong>
            </td>
            <td>
              No tool connects production monitoring back to development
              decisions.
            </td>
            <td>
              Operations Loop (Phase 3) closes the loop from production
              metrics to development priorities.
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        These are not niche feature requests. They are the structural gaps
        that explain why 60% of GenAI projects fail and 95% of pilots never
        reach P&amp;L impact. The market has optimized for code generation
        speed while ignoring everything that determines whether generated code
        creates business value.
      </p>

      <hr />

      <h2>The European advantage</h2>

      <h3>Data sovereignty is real, not marketing</h3>
      <table>
        <thead>
          <tr>
            <th>Data Point</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              61% of Western European CIOs plan to increase reliance on local
              cloud/AI providers
            </td>
            <td>Gartner, 2025</td>
          </tr>
          <tr>
            <td>
              77% of enterprises factor vendor country of origin into AI
              purchasing decisions
            </td>
            <td>Deloitte, 2025</td>
          </tr>
          <tr>
            <td>
              ~20% of European companies began geo-repatriating
              business-critical data
            </td>
            <td>Deloitte, 2025</td>
          </tr>
        </tbody>
      </table>

      <p>
        The market is moving. European enterprises increasingly treat data
        residency and vendor jurisdiction as hard requirements, not
        preferences.
      </p>

      <h3>The gap in the European market</h3>
      <p>
        No pure-play European AI development governance platform exists.
        Lovable (Stockholm) is app generation. Poolside (Paris) and Mistral
        are foundation models. The IDE, assistant, and governance layer
        remains entirely US-dominated.
      </p>
      <p>
        Every European enterprise evaluating AI coding tools today faces the
        same problem: every credible option is a US company, subject to US
        jurisdiction, with no EU data residency guarantee at the
        infrastructure level. For regulated industries — finance, healthcare,
        defense, public sector — this is not an inconvenience. It is a
        blocker.
      </p>

      <h3>Finland's position</h3>
      <p>
        Finland leads the EU in GenAI adoption (66% of firms), yet only 4% of
        Nordic companies achieve strong AI returns (BCG). The gap between
        adoption and value creation is exactly the problem TAIGA solves. No
        Finnish startup builds in AI development governance — TAIGA is first
        to market in a country with world-class AI talent, strong regulatory
        alignment, and deep enterprise software heritage.
      </p>

      <hr />

      <h2>The EU AI Act tailwind</h2>
      <p>The EU AI Act is not a risk. It is a moat.</p>
      <p>
        AI coding tools are classified as General-Purpose AI (GPAI) under the
        Act. GPAI obligations include technical documentation, training data
        summaries, copyright compliance, and transparency. The Act is creating
        a new "Compliance Tech" sub-sector, and regulatory clarity is
        unlocking frozen enterprise budgets.
      </p>

      <h3>Compliance timeline</h3>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Milestone</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>2024</td>
            <td>EU AI Act passed</td>
          </tr>
          <tr>
            <td>Aug 2, 2025</td>
            <td>GPAI obligations in force</td>
          </tr>
          <tr>
            <td>2025</td>
            <td>Enterprises begin compliance planning</td>
          </tr>
          <tr>
            <td>Aug 2, 2026</td>
            <td>
              Enforcement actions begin — penalties up to EUR 35M or 7% of
              global turnover
            </td>
          </tr>
          <tr>
            <td>2027</td>
            <td>Full enforcement — TAIGA platform mature</td>
          </tr>
        </tbody>
      </table>

      <h3>How TAIGA enables compliance</h3>
      <table>
        <thead>
          <tr>
            <th>EU AI Act Requirement</th>
            <th>How TAIGA Addresses It</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Risk assessment</td>
            <td>Automated risk classification in the LEARN loop</td>
          </tr>
          <tr>
            <td>Human oversight</td>
            <td>Human gates at key transitions in the BUILD loop</td>
          </tr>
          <tr>
            <td>Technical documentation</td>
            <td>Auto-generated from governance layer</td>
          </tr>
          <tr>
            <td>Audit trails</td>
            <td>Every agent action is version-controlled and policy-validated</td>
          </tr>
          <tr>
            <td>Transparency</td>
            <td>Full visibility into agent decision-making</td>
          </tr>
          <tr>
            <td>Accountability chains</td>
            <td>
              Clear mapping from business goals to policies to execution to
              outcomes
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        The Brussels Effect means this is not just a European story. Other
        jurisdictions will follow, and multinationals will adopt EU-compliant
        standards globally regardless of where they are headquartered.
        TAIGA's timing aligns precisely with this regulatory wave: 2024 the
        Act passed, 2025 enterprises begin compliance planning, 2026
        compliance becomes mandatory — coinciding with TAIGA's MVP and first
        pilots — and 2027 full enforcement begins as the platform reaches
        maturity.
      </p>
      <p>
        European enterprises face a clear choice: bolt compliance onto
        ungoverned AI tools after the fact, or adopt a governance-first
        platform that makes compliance a byproduct of normal operation. TAIGA
        is built for the second path.
      </p>

      <hr />

      <h2>TAIGA's position</h2>
      <p>
        TAIGA is the only player delivering autonomous AI execution with
        built-in governance, full lifecycle coverage, and outcome-based
        pricing.
      </p>
      <p>
        Every competitor solves a fragment. Cursor makes individuals faster.
        Devin automates tasks. Accenture governs through headcount. None of
        them connect business intent to production software with policy
        enforcement at every step.
      </p>
      <p>TAIGA does.</p>

      <h3>Competitive positioning</h3>
      <p>
        <strong>vs Copilot / Cursor:</strong> For individual productivity,
        these tools work. But can your CISO explain to a regulator what data
        trained them, what policies govern their output, and where the audit
        trail lives? Individual developer speed is a solved problem.
        Enterprise-grade governance of AI-generated code is not.
      </p>
      <p>
        <strong>vs Devin / Factory:</strong> They can write code autonomously.
        Who governs what they write? Who audits the decisions? Who ensures
        they follow organizational policies? Autonomous execution without
        governance is a liability, not an asset.
      </p>
      <p>
        <strong>vs Consultancies:</strong> Same enterprise-grade delivery. 80%
        less cost. 10x faster. Every decision auditable. Consultancies sell
        hours; TAIGA sells outcomes.
      </p>
      <p>
        <strong>vs Build-Your-Own:</strong>
      </p>

      <table>
        <thead>
          <tr>
            <th>Factor</th>
            <th>Build Internally</th>
            <th>Buy TAIGA</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Time to first outcome</td>
            <td>12-18 months</td>
            <td>Weeks</td>
          </tr>
          <tr>
            <td>Engineering investment</td>
            <td>3-5 senior FTEs, ongoing</td>
            <td>Zero</td>
          </tr>
          <tr>
            <td>Governance built-in</td>
            <td>Unlikely (deprioritized)</td>
            <td>Day one</td>
          </tr>
          <tr>
            <td>Maintenance burden</td>
            <td>Permanent</td>
            <td>Included</td>
          </tr>
        </tbody>
      </table>

      <p>
        The build-vs-buy math is stark. No enterprise will invest 18 months of
        senior engineering time to build governance infrastructure when they
        can adopt a purpose-built platform. And in practice, internal
        governance projects are perpetually deprioritized in favor of feature
        work — which is exactly why the gap exists.
      </p>

      <h3>Company details</h3>
      <table>
        <thead>
          <tr>
            <th>Field</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Company</td>
            <td>Taiga AI Oy</td>
          </tr>
          <tr>
            <td>Business ID</td>
            <td>3607851-2</td>
          </tr>
          <tr>
            <td>Jurisdiction</td>
            <td>Finland</td>
          </tr>
          <tr>
            <td>Incorporated</td>
            <td>March 2026</td>
          </tr>
          <tr>
            <td>Stage</td>
            <td>Pre-seed (raising EUR 2M at EUR 20M pre-money)</td>
          </tr>
          <tr>
            <td>HQ</td>
            <td>Helsinki (remote-first)</td>
          </tr>
          <tr>
            <td>Website</td>
            <td>tai.ga</td>
          </tr>
          <tr>
            <td>Target</td>
            <td>EUR 8M ARR by end of 2027</td>
          </tr>
          <tr>
            <td>Infrastructure</td>
            <td>AWS, EU regions only (enforced at org level by SCPs)</td>
          </tr>
          <tr>
            <td>Data residency</td>
            <td>EU-only (infrastructure-enforced, not policy-enforced)</td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
